import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  NONE_DEMO_KIT_VALUE,
  RecipeData,
  useCreateRecipe,
  useCreateRecipeFromDemoKit,
  useGetDemoKitRecipes,
  useUpdateRecipe
} from "../../api/recipeSimulator";
import { FOCUS_RING_CLASS } from "../../config/constants";
import { Button } from "../ui/common/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../ui/common/dialog";
import { Form } from "../ui/common/form";
import { FormInput } from "../ui/Input/FormInput/FormInput";
import { FormSelect } from "../ui/Input/FormSelector/FormSelect";
import { FormTextarea } from "../ui/Input/FormTextArea/FormTextarea";

interface EditRecipeContext {
  recipeId: string;
  recipeData: RecipeData | null | undefined;
}

interface NewRecipeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  editRecipe?: EditRecipeContext;
}

interface NewRecipeFormValues {
  recipeName: string;
  notes: string;
  demoKit: string;
}

const DEMO_KIT_PAGE_NUMBER = 1;
const DEMO_KIT_PAGE_SIZE = 20;


const NewRecipeDialog = ({ open, onOpenChange, onSuccess, editRecipe }: NewRecipeDialogProps) => {
  const navigate = useNavigate();
  const isEditMode = Boolean(editRecipe);
  const form = useForm<NewRecipeFormValues>({
    defaultValues: {
      recipeName: "",
      notes: "",
      demoKit: NONE_DEMO_KIT_VALUE
    },
    mode: "onChange"
  });

  const { handleSubmit, reset, control } = form;
  const recipeName = useWatch({ control, name: "recipeName", defaultValue: "" });

  const { data: demoKits = [], isLoading: isDemoKitsLoading } = useGetDemoKitRecipes(
    DEMO_KIT_PAGE_NUMBER,
    DEMO_KIT_PAGE_SIZE,
    "",
    open && !isEditMode
  );

  const hasDemoKits = demoKits.length > 0;
  const isDemoKitUnavailable = !isDemoKitsLoading && !hasDemoKits;
  const demoKitName = editRecipe?.recipeData?.demoKitDetails?.demoKitName || "None (Start from scratch)";


  const demoKitOptions = useMemo(() => {
    if (!hasDemoKits) {
      return [];
    }

    return [
      { value: NONE_DEMO_KIT_VALUE, text: "None" },
      ...demoKits.map(kit => ({
        value: String(kit.id),
        text: kit.title
      }))
    ];
  }, [demoKits, hasDemoKits]);


  useEffect(() => {
    if (open) {
      reset({
        recipeName: editRecipe?.recipeData?.recipeName ?? "",
        notes: editRecipe?.recipeData?.notes ?? "",
        demoKit: NONE_DEMO_KIT_VALUE
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  const { mutateAsync: createRecipe, isLoading: isCreatingRecipe } = useCreateRecipe();
  const { mutateAsync: createRecipeFromDemoKit, isLoading: isCreatingFromDemoKit } =
    useCreateRecipeFromDemoKit();
  const { mutate: updateRecipe, isLoading: isUpdatingRecipe } = useUpdateRecipe();

  const isLoading = isCreatingRecipe || isCreatingFromDemoKit || isUpdatingRecipe;
  const canSubmit = Boolean(recipeName.trim()) && !isLoading;

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const onSubmit = async (data: NewRecipeFormValues) => {
    const payload = {
      recipeName: data.recipeName.trim(),
      notes: data.notes.trim()
    };

    if (isEditMode && editRecipe) {
      updateRecipe(
        { recipeId: editRecipe.recipeId, recipe: payload },
        {
          onSuccess: () => {
            handleClose();
            onSuccess?.();
          }
        }
      );
      return;
    }

    try {
      const newRecipe =
        data.demoKit !== NONE_DEMO_KIT_VALUE
          ? await createRecipeFromDemoKit({
              demoKitId: data.demoKit,
              recipe: payload
            })
          : await createRecipe(payload);

      handleClose();
      onSuccess?.();
      if (newRecipe?._id) {
        navigate(`/recipe-simulator/${newRecipe._id}/manage`);
      }
    } catch {
      // Error handling is managed by the API layer
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-max-w-md">
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{isEditMode ? "Recipe Notes" : "Create New Recipe"}</DialogTitle>
              <DialogDescription>
                {isEditMode
                  ? "View and update the notes captured when this recipe was registered."
                  : "Enter recipe details and optionally select a demo kit as a starting point."}
              </DialogDescription>
            </DialogHeader>
            <div className="tw-grid tw-gap-4 tw-py-4">
              <FormInput
                control={control}
                name="recipeName"
                label="Recipe Name"
                placeholder="Enter recipe name"
                required
                errorMessage="Recipe name is required"
                className={FOCUS_RING_CLASS}
                rules={{
                  validate: value =>
                    (typeof value === "string" && value.trim() !== "") || "Recipe name is required"
                }}
              />
              <FormTextarea
                control={control}
                name="notes"
                label="Note/Description"
                placeholder="Optional notes"
                className={FOCUS_RING_CLASS}
              />
              {isEditMode ? (
                <div className="tw-space-y-2">
                  <label className="tw-text-sm tw-font-medium tw-text-foreground">Based on Demo Kit</label>
                  <div
                    className="tw-flex tw-h-10 tw-w-full tw-items-center tw-rounded-md tw-border tw-border-gray-300 tw-bg-muted/40 tw-px-3 tw-text-sm tw-text-muted-foreground"
                    aria-disabled="true"
                    aria-readonly="true"
                  >
                    {demoKitName}
                  </div>
                </div>
              ) : isDemoKitsLoading ? (
                <div className="tw-space-y-2">
                  <label className="tw-text-sm tw-font-medium tw-text-foreground">Based on Demo Kit</label>
                  <div className="tw-flex tw-h-10 tw-w-full tw-items-center tw-rounded-md tw-border tw-border-gray-300 tw-bg-muted/40 tw-px-3 tw-text-sm tw-text-muted-foreground">
                    Loading demo kits...
                  </div>
                </div>
              ) : (
                <FormSelect
                  control={control}
                  name="demoKit"
                  label="Based on Demo Kit"
                  placeholder={
                    isDemoKitUnavailable ? "Demo kit not available" : "Select demo kit"
                  }
                  options={demoKitOptions}
                  disabled={isDemoKitUnavailable}
                />
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {isLoading ? "Saving..." : isEditMode ? "Save Notes" : "Create Recipe"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default NewRecipeDialog;
